import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import apiClient from "@/lib/api-client";
import { useGlobalToast } from "@/contexts/ToastContext";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { Aviso, Cargando, FilaHecha, INPUT_CLASS, LABEL_CLASS } from "./ui";
import type { PasoProps } from "./types";

/**
 * PASO 2 (citas): los servicios. Cada uno con su DURACIÓN, que es lo que decide
 * cuántos huecos caben en el día; sin al menos un servicio activo el widget no
 * tiene nada que ofrecer.
 *
 * Aquí solo se piden las tres cosas imprescindibles (nombre, duración y
 * precio). Todo lo demás —categoría, anticipo, color, descripción— se queda
 * para la pantalla de Servicios: este paso tiene que poder terminarse en medio
 * minuto.
 */

interface Servicio {
  id: string;
  name: string;
  durationMinutes: number;
  price: number | null;
}

const DURACIONES = [15, 20, 30, 45, 60, 90, 120];

export default function PasoServicios({
  refrescar,
  registrarAvance,
  registrarTextoAvance,
}: PasoProps) {
  const { showToast } = useGlobalToast();
  const [servicios, setServicios] = useState<Servicio[] | null>(null);
  const [nombre, setNombre] = useState("");
  const [duracion, setDuracion] = useState(30);
  const [precio, setPrecio] = useState("");
  const [creando, setCreando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const res = await apiClient.get("/services", {
        params: { includeInactive: "false" },
      });
      setServicios(res.data?.data ?? []);
    } catch {
      setServicios([]);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const crear = async () => {
    const limpio = nombre.trim();
    if (!limpio) {
      showToast("Escribe el nombre del servicio", "error");
      return;
    }
    const importe = precio.trim() === "" ? 0 : Number(precio.replace(",", "."));
    if (Number.isNaN(importe) || importe < 0) {
      showToast("El precio tiene que ser un número", "error");
      return;
    }
    try {
      setCreando(true);
      await apiClient.post("/services", {
        name: limpio,
        durationMinutes: duracion,
        price: importe,
        isActive: true,
      });
      setNombre("");
      setPrecio("");
      await cargar();
      refrescar();
      showToast("Servicio creado");
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo crear el servicio"),
        "error",
      );
    } finally {
      setCreando(false);
    }
  };

  const avanzar = useCallback(async (): Promise<boolean> => {
    if ((servicios?.length ?? 0) === 0) {
      showToast(
        "Crea al menos un servicio: es lo que tus clientes eligen al pedir cita",
        "error",
      );
      return false;
    }
    return true;
  }, [servicios, showToast]);

  useEffect(() => {
    registrarAvance(avanzar);
    registrarTextoAvance(null);
    return () => registrarAvance(null);
  }, [avanzar, registrarAvance, registrarTextoAvance]);

  if (!servicios) return <Cargando texto="Cargando tus servicios…" />;

  return (
    <div className="space-y-4">
      <Aviso>
        Un servicio es lo que viene a hacerse tu cliente: <em>corte de pelo</em>,{" "}
        <em>primera consulta</em>, <em>manicura</em>… La <strong>duración</strong>{" "}
        es lo importante: con ella se calculan las horas libres que se ofrecen.
      </Aviso>

      {servicios.length > 0 && (
        <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 px-3">
          {servicios.map((s) => (
            <FilaHecha
              key={s.id}
              titulo={s.name}
              detalle={`${s.durationMinutes} min${
                s.price ? ` · ${s.price} €` : ""
              }`}
            />
          ))}
        </div>
      )}

      <div className="rounded-lg border border-dashed border-gray-300 p-3 space-y-3">
        <p className="text-sm font-medium text-gray-900">
          {servicios.length === 0 ? "Crea tu primer servicio" : "Añadir otro servicio"}
        </p>
        <div>
          <label className={LABEL_CLASS} htmlFor="wizard-servicio-nombre">
            Nombre
          </label>
          <input
            id="wizard-servicio-nombre"
            className={INPUT_CLASS}
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Corte de pelo"
            maxLength={100}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL_CLASS} htmlFor="wizard-servicio-duracion">
              ¿Cuánto dura?
            </label>
            <select
              id="wizard-servicio-duracion"
              className={INPUT_CLASS}
              value={duracion}
              onChange={(e) => setDuracion(Number(e.target.value))}
            >
              {DURACIONES.map((d) => (
                <option key={d} value={d}>
                  {d} minutos
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="wizard-servicio-precio">
              Precio (€)
            </label>
            <input
              id="wizard-servicio-precio"
              className={INPUT_CLASS}
              value={precio}
              onChange={(e) => setPrecio(e.target.value)}
              placeholder="20"
              inputMode="decimal"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={crear}
          disabled={creando}
          className="inline-flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          {creando ? "Creando…" : "Añadir servicio"}
        </button>
      </div>
    </div>
  );
}
